import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import { Flame, Heart, MessageCircle, Users, User as UserIcon, LogOut, Crown, BookOpen, LifeBuoy, HandHeart } from "lucide-react";

const NAV = [
  { to: "/discover", key: "discover", testid: "nav-discover-link", icon: Flame },
  { to: "/likes", key: "likes", testid: "nav-likes-link", icon: Heart },
  { to: "/matches", key: "matches", testid: "nav-matches-link", icon: Users },
  { to: "/messages", key: "messages", testid: "nav-messages-link", icon: MessageCircle },
  { to: "/forum", key: "community", testid: "nav-forum-link", icon: UserIcon },
  { to: "/awareness", key: "awareness", testid: "nav-awareness-link", icon: BookOpen },
  { to: "/health", key: "health", testid: "nav-health-link", icon: HandHeart },
  { to: "/premium", key: "premium", testid: "nav-premium-link", icon: Crown },
  { to: "/help", key: "help", testid: "nav-help-link", icon: LifeBuoy },
  { to: "/profile", key: "profile", testid: "nav-profile-link", icon: UserIcon },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-[#E8DEC8] bg-[#FFFDF9]/85 backdrop-blur-md dark:bg-[#1A1615]/85 dark:border-[#423B36]">
      {user && user.email_verified === false && (
        <Link to="/verify-email" data-testid="verify-email-nudge"
          className="block w-full bg-[#FEF3D6] text-[#B45309] text-center text-xs sm:text-sm py-1.5 px-4 hover:bg-[#F5D77E] transition-colors">
          ✉️ {t("nav.verify_email_banner")}
        </Link>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/discover" data-testid="nav-brand-logo" className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D95D39] to-[#E88796] flex items-center justify-center text-white">
            <Heart className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <span className="font-serif text-xl font-bold text-[#2C2623] dark:text-[#FAF5F0]">HerpMates</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map(({ to, key, testid, icon: Icon }) => {
            const active = pathname.startsWith(to);
            return (
              <Link key={to} to={to} data-testid={testid}
                className={`px-3 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${
                  active
                    ? "bg-[#FFF0EC] text-[#C24C29] dark:bg-[#3A211B] dark:text-[#E07A5F]"
                    : "text-[#6E655F] hover:text-[#2C2623] hover:bg-[#F9F5EE] dark:text-[#B5AAA1] dark:hover:bg-[#302A27]"
                }`}>
                <Icon className="w-4 h-4" />
                {t(`nav.${key}`)}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          <LanguageSwitcher />
          {user && (
            <Avatar className="w-9 h-9 ring-2 ring-[#FFF0EC] dark:ring-[#3A211B]">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-[#D95D39] text-white text-sm">
                {user.name?.[0]?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
          )}
          <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="nav-logout-btn" title={t("nav.sign_out")}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <nav className="md:hidden flex items-center gap-1 px-2 py-2 border-t border-[#E8DEC8] dark:border-[#423B36] overflow-x-auto no-scrollbar">
        {NAV.map(({ to, key, testid, icon: Icon }) => {
          const active = pathname.startsWith(to);
          return (
            <Link key={to} to={to} data-testid={`${testid}-mobile`}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[11px] flex-shrink-0 min-w-[56px] ${
                active ? "text-[#C24C29] dark:text-[#E07A5F]" : "text-[#6E655F] dark:text-[#B5AAA1]"
              }`}>
              <Icon className="w-4 h-4" />
              {t(`nav.${key}`)}
            </Link>
          );
        })}
      </nav>
    </header>
  );
}
